import { useState, useCallback, useRef, useEffect } from 'react';

const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 3;

function generateSessionToken() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

/**
 * Hook that proxies Google Places autocomplete through the backend,
 * keeping the API key server-side.
 *
 * @param {Object} params
 * @param {Function} params.onPlaceSelected - Called with the Google Place object on selection
 * @param {Object}  [params.options] - Options matching the Google Autocomplete widget shape
 * @param {string[]} [params.options.types] - Place types (default: ['geocode'])
 * @param {Object}  [params.options.componentRestrictions] - { country: 'us' | ['us','ca'] }
 * @param {string}  [params.options.language] - BCP-47 language code (default: 'en')
 */
export default function usePlacesProxy({ onPlaceSelected, options = {} } = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const sessionTokenRef = useRef(generateSessionToken());

  const country = options.componentRestrictions?.country;
  const types = options.types;
  const language = options.language || 'en';

  const fetchSuggestions = useCallback((input) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!input || input.length < MIN_INPUT_LENGTH) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      setIsLoading(true);
      try {
        const countryParam = Array.isArray(country) ? country.join(',') : (country || 'us');
        const typesParam = Array.isArray(types) ? types.join(',') : (types || 'geocode');

        const params = new URLSearchParams({
          input,
          country: countryParam,
          types: typesParam,
          language,
          sessiontoken: sessionTokenRef.current,
        });

        const response = await fetch(`/api/google/places/autocomplete?${params}`, {
          signal: abortRef.current.signal,
        });
        const data = await response.json();
        setSuggestions(data.predictions || []);
        setShowSuggestions((data.predictions || []).length > 0);
        setActiveIndex(-1);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setSuggestions([]);
        }
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [country, types]);

  const selectPlace = useCallback(async (prediction) => {
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const params = new URLSearchParams({ place_id: prediction.place_id });
      const response = await fetch(`/api/google/places/details?${params}`);
      const data = await response.json();

      if (data.result && onPlaceSelected) {
        onPlaceSelected(data.result);
      }

      return data.result || null;
    } catch (e) {
      return null;
    }
  }, [onPlaceSelected]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          e.preventDefault();
          selectPlace(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  }, [showSuggestions, suggestions, activeIndex, selectPlace]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return {
    suggestions,
    showSuggestions,
    activeIndex,
    isLoading,
    fetchSuggestions,
    selectPlace,
    clearSuggestions,
    handleKeyDown,
    containerRef,
  };
}
